import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert, Spinner } from 'react-bootstrap';
import api from '../../services/api';

const MemberImportModal = ({ show, handleClose, onImportSuccess }) => {
  // State for file upload
  const [importFiles, setImportFiles] = useState([]);
  const [importLoading, setImportLoading] = useState(false);
  const [importError, setImportError] = useState(null);
  const [importSuccess, setImportSuccess] = useState(null);
  const [selectedAssociation, setSelectedAssociation] = useState('');
  const [associations, setAssociations] = useState([]);
  const [loadingAssociations, setLoadingAssociations] = useState(false);
  
  // Batch import states
  const [batchId, setBatchId] = useState(null);
  const [batchStatus, setBatchStatus] = useState(null);
  const [isPolling, setIsPolling] = useState(false);

  // Fetch associations on component mount
  useEffect(() => {
    if (show) {
      fetchAssociations();
    }
  }, [show]);

  // Fetch associations list
  const fetchAssociations = async () => {
    try {
      setLoadingAssociations(true);
      const token = localStorage.getItem('token');
      const response = await api.get('/admin/associations', {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Verifica se la risposta ha la struttura corretta
      if (response.data && response.data.success && Array.isArray(response.data.associations)) {
        console.log('Associations fetched successfully:', response.data.associations.length);
        setAssociations(response.data.associations);
      } else {
        console.error('Incorrect response format from API:', response.data);
        // Imposta un array vuoto per evitare errori
        setAssociations([]);
      }

      setLoadingAssociations(false);
    } catch (err) {
      console.error('Error fetching associations:', err);
      // Imposta un array vuoto in caso di errore
      setAssociations([]);
      setLoadingAssociations(false);
    }
  };

  // Handle file change (multiple files)
  const handleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      setImportFiles(Array.from(e.target.files));
      // Reset previous results when new files are selected
      setBatchId(null);
      setBatchStatus(null);
      setImportError(null);
      setImportSuccess(null);
    }
  };

  // Remove a file from the list
  const removeFile = (index) => {
    setImportFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Handle association selection change
  const handleAssociationChange = (e) => {
    setSelectedAssociation(e.target.value);
  };

  // Poll for batch status
  const pollBatchStatus = async (currentBatchId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await api.get(`/members/import/batch/${currentBatchId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success && response.data.batchStatus) {
        setBatchStatus(response.data.batchStatus);

        // If batch is complete, stop polling
        if (response.data.batchStatus.isComplete) {
          setIsPolling(false);
          setImportLoading(false);
          
          const { completed, failed, totalFiles } = response.data.batchStatus;
          setImportSuccess(`Importazione completata: ${completed} file elaborati con successo, ${failed} file con errori su ${totalFiles} totali`);
          
          // Call parent success handler
          if (onImportSuccess) {
            onImportSuccess(response.data.batchStatus);
          }
        }
      }
    } catch (err) {
      console.error('Error polling batch status:', err);
      setIsPolling(false);
      setImportLoading(false);
    }
  };

  // Start polling when batch is created
  useEffect(() => {
    if (isPolling && batchId) {
      const interval = setInterval(() => {
        pollBatchStatus(batchId);
      }, 2000); // Poll every 2 seconds

      return () => clearInterval(interval);
    }
  }, [isPolling, batchId]);

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!importFiles || importFiles.length === 0) {
      setImportError('Seleziona almeno un file da importare');
      return;
    }

    try {
      setImportLoading(true);
      setImportError(null);
      setImportSuccess(null);

      const formData = new FormData();
      
      // Add all files to form data
      importFiles.forEach((file) => {
        formData.append('files', file);
      });
      
      // Aggiungi l'associazione solo se selezionata
      if (selectedAssociation) {
        formData.append('association_id', selectedAssociation);
      }

      const token = localStorage.getItem('token');
      const response = await api.post('/members/import/batch', formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success && response.data.batchId) {
        setBatchId(response.data.batchId);
        setBatchStatus(response.data.batchStatus);
        setIsPolling(true);
        setImportSuccess(`${importFiles.length} file aggiunti alla coda di importazione...`);
      }

    } catch (err) {
      console.error('Error importing members:', err);
      setImportError(
        err.response?.data?.message ||
        'Errore durante l\'importazione. Verifica il formato dei file.'
      );
      setImportLoading(false);
    }
  };

  // Handle modal close and reset state
  const handleModalClose = () => {
    setImportFiles([]);
    setImportError(null);
    setImportSuccess(null);
    setBatchId(null);
    setBatchStatus(null);
    setIsPolling(false);
    handleClose();
  };

  return (
    <Modal show={show} onHide={handleModalClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Importa Soci</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p>
          Utilizzare questa funzione per importare i soci da un file Excel (formato .xlsx).
        </p>
        <p>
          Il file deve contenere i seguenti campi obbligatori:
        </p>
        <ul>
          <li><strong>Nome</strong> - Nome del socio</li>
          <li><strong>Cognome</strong> - Cognome del socio</li>
          <li><strong>Codice Fiscale</strong> - Codice fiscale del socio</li>
        </ul>

        <Form onSubmit={handleSubmit}>
          <Form.Group className="mb-3">
            <Form.Label>Seleziona Associazione (opzionale)</Form.Label>
            <Form.Select
              value={selectedAssociation}
              onChange={handleAssociationChange}
              disabled={loadingAssociations}
            >
              <option value="">Tutte le associazioni dal file</option>
              {Array.isArray(associations) ? associations.map(association => (
                <option key={association.id} value={association.id}>
                  {association.name || association.username}
                </option>
              )) : (
                <option value="" disabled>Nessuna associazione disponibile</option>
              )}
            </Form.Select>
            {loadingAssociations && (
              <div className="text-center mt-2">
                <Spinner animation="border" size="sm" />
                <span className="ms-2">Caricamento associazioni...</span>
              </div>
            )}
            <Form.Text className="text-muted">
              Se non selezioni un'associazione, i membri verranno associati automaticamente alle associazioni presenti nel file (campo "cod_affiliata").
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Seleziona file (puoi selezionare più file)</Form.Label>
            <Form.Control
              type="file"
              onChange={handleFileChange}
              accept=".xlsx, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              multiple
              disabled={importLoading}
            />
            <Form.Text className="text-muted">
              Accettati solo file Excel (.xlsx). Puoi selezionare più file contemporaneamente.
            </Form.Text>
          </Form.Group>

          {importFiles.length > 0 && !batchStatus && (
            <div className="mb-3">
              <strong>File selezionati ({importFiles.length}):</strong>
              <ul className="list-group mt-2">
                {importFiles.map((file, index) => (
                  <li key={index} className="list-group-item d-flex justify-content-between align-items-center">
                    <span>
                      {file.name}
                      <small className="text-muted ms-2">
                        ({(file.size / 1024).toFixed(2)} KB)
                      </small>
                    </span>
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => removeFile(index)}
                      disabled={importLoading}
                    >
                      ×
                    </Button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {batchStatus && (
            <div className="mb-3">
              <strong>Stato elaborazione:</strong>
              <div className="mt-2 p-3 border rounded">
                <div className="mb-2">
                  <strong>Totale file:</strong> {batchStatus.totalFiles}
                </div>
                <div className="mb-2">
                  <strong>Completati:</strong> <span className="text-success">{batchStatus.completed}</span>
                </div>
                <div className="mb-2">
                  <strong>In elaborazione:</strong> <span className="text-primary">{batchStatus.processing}</span>
                </div>
                <div className="mb-2">
                  <strong>In coda:</strong> <span className="text-secondary">{batchStatus.pending}</span>
                </div>
                <div className="mb-2">
                  <strong>Con errori:</strong> <span className="text-danger">{batchStatus.failed}</span>
                </div>

                {batchStatus.jobs && batchStatus.jobs.length > 0 && (
                  <div className="mt-3">
                    <strong>Dettaglio file:</strong>
                    <ul className="list-group mt-2" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                      {batchStatus.jobs.map((job, index) => (
                        <li key={job.id} className="list-group-item">
                          <div className="d-flex justify-content-between align-items-start">
                            <div>
                              <strong>{job.fileName}</strong>
                              <div className="small">
                                {job.status === 'completed' && (
                                  <span className="text-success">
                                    ✓ Completato - {job.result?.successCount || 0} importati, {job.result?.errorCount || 0} errori
                                  </span>
                                )}
                                {job.status === 'processing' && (
                                  <span className="text-primary">
                                    <Spinner animation="border" size="sm" className="me-1" />
                                    In elaborazione...
                                  </span>
                                )}
                                {job.status === 'pending' && (
                                  <span className="text-secondary">In attesa...</span>
                                )}
                                {job.status === 'failed' && (
                                  <span className="text-danger">✗ Errore: {job.error}</span>
                                )}
                              </div>
                            </div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          )}

          {importError && (
            <Alert variant="danger" className="mt-3">
              {importError}
            </Alert>
          )}

          {importSuccess && (
            <Alert variant="success" className="mt-3">
              {importSuccess}
            </Alert>
          )}

          <div className="d-flex justify-content-end mt-4">
            <Button 
              variant="secondary" 
              onClick={handleModalClose} 
              className="me-2"
              disabled={importLoading && !batchStatus?.isComplete}
            >
              {batchStatus?.isComplete ? 'Chiudi' : 'Annulla'}
            </Button>
            <Button
              variant="primary"
              type="submit"
              disabled={importFiles.length === 0 || importLoading}
            >
              {importLoading ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                    className="me-2"
                  />
                  {isPolling ? `Elaborazione in corso...` : 'Caricamento...'}
                </>
              ) : (
                `Importa ${importFiles.length} ${importFiles.length === 1 ? 'File' : 'File'}`
              )}
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default MemberImportModal;
